import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { courseService, Course } from '../../api/courseService';
import { theme } from '../../styles/theme';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import { AxiosResponse } from 'axios';

const CoursesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const Header = styled.div`
  margin-bottom: ${theme.spacing.md};
  
  h1 {
    font-size: ${theme.fontSizes.xxlarge};
    margin-bottom: ${theme.spacing.sm};
  }
  
  p {
    font-size: ${theme.fontSizes.medium};
    color: ${theme.colors.gray};
  }
`;

const CoursesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const CourseCard = styled(Card)`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const CourseImage = styled.div`
  height: 160px;
  overflow: hidden;
  margin: -${theme.spacing.md} -${theme.spacing.md} ${theme.spacing.md} -${theme.spacing.md};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CourseContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  
  h2 {
    font-size: ${theme.fontSizes.large};
    margin-bottom: ${theme.spacing.xs};
  }
  
  p {
    font-size: ${theme.fontSizes.medium};
    color: ${theme.colors.gray};
    margin-bottom: ${theme.spacing.md};
    flex: 1;
  }
`;

const EnrollmentStatus = styled.div`
  background-color: ${theme.colors.light};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.radii.small};
  margin-bottom: ${theme.spacing.sm};
  font-size: ${theme.fontSizes.small};
  display: inline-block;
  color: ${theme.colors.primary};
`;

const CourseActions = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
`;

const LoadingContainer = styled.div`
  padding: ${theme.spacing.lg};
  text-align: center;
  color: ${theme.colors.gray};
`;

const ErrorContainer = styled.div`
  padding: ${theme.spacing.md};
  color: ${theme.colors.danger};
  text-align: center;
`;

const NoCoursesContainer = styled.div`
  padding: ${theme.spacing.lg};
  text-align: center;
  
  p {
    margin-bottom: ${theme.spacing.md};
    color: ${theme.colors.gray};
  }
`;

const CoursesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [courseEnrollmentMap, setCourseEnrollmentMap] = useState<Record<string, boolean>>({});
  
  // Get available courses with enrollment status
  const { data, isLoading, error } = useQuery<AxiosResponse<{success: boolean, data: Course[]}>, Error>({
    queryKey: ['availableCourses'],
    queryFn: () => courseService.getAvailableCourses(),
  });
  
  // Get enrolled courses to double-check enrollment status
  const { data: enrolledCoursesData } = useQuery({
    queryKey: ['enrolledCourses'],
    queryFn: () => courseService.getEnrolledCourses(),
  });
  
  // Check enrollment status manually from enrolledCourses
  useEffect(() => {
    if (enrolledCoursesData?.data?.data) {
      const enrolledCourses = enrolledCoursesData.data.data;
      const enrollmentMap: Record<string, boolean> = {};
      
      enrolledCourses.forEach(enrollment => {
        enrollmentMap[String(enrollment.course_id)] = true;
      });
      
      console.log('Manual enrollment map:', enrollmentMap);
      setCourseEnrollmentMap(enrollmentMap);
    }
  }, [enrolledCoursesData]);

  // Enrollment mutation
  const enrollMutation = useMutation({
    mutationFn: (courseId: string) => 
      courseService.enrollInCourse(courseId),
    onSuccess: async () => {
      // Invalidate and refetch queries
      await queryClient.invalidateQueries({ queryKey: ['availableCourses'] });
      await queryClient.invalidateQueries({ queryKey: ['enrolledCourses'] });
      
      // Force refetch
      await queryClient.refetchQueries({ queryKey: ['availableCourses'] });
    },
  });

  // Unenrollment mutation
  const unenrollMutation = useMutation({
    mutationFn: (courseId: string) => 
      courseService.unenrollFromCourse(courseId),
    onSuccess: async () => {
      // Invalidate and refetch queries
      await queryClient.invalidateQueries({ queryKey: ['availableCourses'] });
      await queryClient.invalidateQueries({ queryKey: ['enrolledCourses'] });
      
      // Force refetch
      await queryClient.refetchQueries({ queryKey: ['availableCourses'] });
    },
  });

  // Handle enrollment
  const handleEnroll = (courseId: string) => {
    enrollMutation.mutate(courseId);
  };

  // Handle unenrollment
  const handleUnenroll = (courseId: string) => {
    if (window.confirm('Are you sure you want to unenroll from this course? Your progress will be saved, but you will need to enroll again to access the content.')) {
      unenrollMutation.mutate(courseId);
    }
  };

  if (isLoading) {
    return (
      <CoursesContainer>
        <Header>
          <h1>Courses</h1>
          <p>Grow in your faith through our curated learning experiences</p>
        </Header>
        <LoadingContainer>Loading courses...</LoadingContainer>
      </CoursesContainer>
    );
  }

  if (error) {
    console.error('Courses error:', error);
    return (
      <CoursesContainer>
        <Header>
          <h1>Courses</h1>
          <p>Grow in your faith through our curated learning experiences</p>
        </Header>
        <ErrorContainer>
          Unable to load courses. Please try again later.
        </ErrorContainer>
      </CoursesContainer>
    );
  }

  const courses = data?.data?.data || [];

  if (courses.length === 0) {
    return (
      <CoursesContainer>
        <Header>
          <h1>Courses</h1>
          <p>Grow in your faith through our curated learning experiences</p>
        </Header>
        <NoCoursesContainer>
          <p>No courses available at this time. Check back soon!</p>
        </NoCoursesContainer>
      </CoursesContainer>
    );
  }

  return (
    <CoursesContainer>
      <Header>
        <h1>Courses</h1>
        <p>Grow in your faith through our curated learning experiences</p>
      </Header>
      
      <CoursesList>
        {courses.map((course: Course) => {
          // Use course.is_enrolled if available, otherwise use our manual check
          const isEnrolled = course.is_enrolled !== undefined 
            ? course.is_enrolled 
            : courseEnrollmentMap[course.id] || false;
            
          return (
            <CourseCard key={course.id}>
              <CourseImage>
                <img src={course.coverImage || course.thumbnail_url || '/placeholdercourse.png'} alt={course.title} />
              </CourseImage>
              <CourseContent>
                <h2>{course.title}</h2>
                {isEnrolled && (
                  <EnrollmentStatus>
                    Enrolled
                  </EnrollmentStatus>
                )}
                <p>{course.description}</p>
                
                <CourseActions>
                  {isEnrolled ? (
                    <>
                      <Button as={Link} to={`/courses/${course.id}`}>
                        Continue Learning
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => handleUnenroll(course.id)}
                        disabled={unenrollMutation.isPending}
                      >
                        Unenroll
                      </Button>
                    </>
                  ) : (
                    <Button 
                      onClick={() => handleEnroll(course.id)}
                      disabled={enrollMutation.isPending}
                    >
                      Enroll Now
                    </Button>
                  )}
                </CourseActions>
              </CourseContent>
            </CourseCard>
          );
        })}
      </CoursesList>
    </CoursesContainer>
  );
};

export default CoursesPage; 